import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Fade } from 'react-awesome-reveal';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { trackEvent } from '../../helpers';
import { EVENTS, getUpcomingEvents } from '../../content/events';
import { MANCHESTER_EVENTS, getUpcomingEvents as getManchesterUpcomingEvents } from '../../content/manchester-events';
import {
  PageWrapper,
  Wrap,
  Eyebrow,
  HeroSection,
  HeroTitle,
  HeroSub,
  FiltersContainer,
  FilterChip,
  ResourcesGrid,
  ResourceCard,
  CardEyebrow,
  CardTitle,
  CardDescription,
  CardMeta,
  CardArrow,
  FooterSection,
} from './resources.styles';

type FilterType = 'all' | 'events' | 'career' | 'guide';

interface Resource {
  id: string;
  type: 'events' | 'career' | 'guide';
  eyebrow: string;
  title: string;
  titleHighlight: string;
  description: string;
  meta: { label: string; value: string }[];
  link: string;
  isExternal?: boolean;
  trackingEvent: string;
  cta: string;
}

export const Resources = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  // Get event counts
  const londonEventsCount = getUpcomingEvents(EVENTS).length;
  const manchesterEventsCount = getManchesterUpcomingEvents(MANCHESTER_EVENTS).length;

  const resources: Resource[] = useMemo(() => [
    {
      id: 'london-events',
      type: 'events',
      eyebrow: 'Event Calendar',
      title: 'London Tech Events',
      titleHighlight: 'worth your time.',
      description: 'A hand-picked calendar for people breaking into tech, building something or growing their network. Careers fairs, founder nights, AI conferences and community events.',
      meta: [
        { label: 'upcoming events', value: String(londonEventsCount) },
        { label: '', value: 'Autumn 2026' }
      ],
      link: '/resources/london-tech-events',
      trackingEvent: 'Resources - London Events Widget Click',
      cta: 'View events'
    },
    {
      id: 'manchester-events',
      type: 'events',
      eyebrow: 'Event Calendar',
      title: 'Manchester Tech Events',
      titleHighlight: 'worth your time.',
      description: 'A hand-picked calendar for the Manchester tech scene. AI conferences, cybersecurity events, data meetups, Black History Month celebrations and community networking.',
      meta: [
        { label: 'upcoming events', value: String(manchesterEventsCount) },
        { label: '', value: 'Autumn 2026' }
      ],
      link: '/resources/manchester-tech-events',
      trackingEvent: 'Resources - Manchester Events Widget Click',
      cta: 'View events'
    },
    {
      id: 'bragbooq-resources',
      type: 'career',
      eyebrow: 'Career Tools',
      title: 'Free career resources',
      titleHighlight: 'to help you level up.',
      description: 'Templates, guides and tools to help you track your wins, prep for interviews, and make your next career move with confidence.',
      meta: [
        { label: '', value: 'Free' },
        { label: '', value: 'By Bragbooq' }
      ],
      link: 'https://bragbooq.com/resources',
      isExternal: true,
      trackingEvent: 'Resources - Bragbooq Resources Widget Click',
      cta: 'View resources'
    },
    {
      id: 'the-guide',
      type: 'guide',
      eyebrow: 'Ebook',
      title: 'The Unhinged Guide',
      titleHighlight: 'to Breaking Into Tech.',
      description: 'A no-fluff playbook for landing your first tech role. Networking scripts, CV templates, interview prep, and a 30-day action plan.',
      meta: [
        { label: '', value: '£39' },
        { label: '', value: '22 worksheets' }
      ],
      link: '/the-guide',
      trackingEvent: 'Resources - The Guide Widget Click',
      cta: 'Get the guide'
    }
  ], [londonEventsCount, manchesterEventsCount]);

  const filteredResources = useMemo(() => {
    if (activeFilter === 'all') return resources;
    return resources.filter(r => r.type === activeFilter);
  }, [resources, activeFilter]);

  const filterCounts = useMemo(() => ({
    all: resources.length,
    events: resources.filter(r => r.type === 'events').length,
    career: resources.filter(r => r.type === 'career').length,
    guide: resources.filter(r => r.type === 'guide').length,
  }), [resources]);

  useEffect(() => {
    trackEvent('Resources - Page View');
  }, []);

  const handleCardClick = (resource: Resource) => (e: React.MouseEvent) => {
    e.preventDefault();
    trackEvent(resource.trackingEvent);
    if (resource.isExternal) {
      window.open(resource.link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(resource.link);
    }
  };

  const handleFilterClick = (filter: FilterType) => {
    setActiveFilter(filter);
    trackEvent('Resources - Filter Click', { filter });
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>Resources | Renee Louise</title>
        <meta name="description" content="Free resources for tech professionals: curated tech event calendars for London and Manchester, guides, and tools to help you grow your career." />
        <link rel="canonical" href="https://reneelouise.tech/resources" />
      </Helmet>

      <main>
        <Wrap>
          <HeroSection>
            <Fade triggerOnce>
              <Eyebrow>Resources</Eyebrow>
              <HeroTitle>Tools and guides <em>to help you grow.</em></HeroTitle>
              <HeroSub>
                Curated resources for tech professionals: event calendars, career guides, and practical tools to help you break in, level up, and build your network.
              </HeroSub>
            </Fade>
          </HeroSection>

          <FiltersContainer>
            <FilterChip
              $active={activeFilter === 'all'}
              onClick={() => handleFilterClick('all')}
            >
              All <span>{filterCounts.all}</span>
            </FilterChip>
            <FilterChip
              $active={activeFilter === 'events'}
              onClick={() => handleFilterClick('events')}
            >
              Events <span>{filterCounts.events}</span>
            </FilterChip>
            <FilterChip
              $active={activeFilter === 'career'}
              onClick={() => handleFilterClick('career')}
            >
              Career <span>{filterCounts.career}</span>
            </FilterChip>
            <FilterChip
              $active={activeFilter === 'guide'}
              onClick={() => handleFilterClick('guide')}
            >
              Guides <span>{filterCounts.guide}</span>
            </FilterChip>
          </FiltersContainer>

          <ResourcesGrid>
            {filteredResources.map((resource, index) => (
              <Fade key={resource.id} delay={index * 100} triggerOnce>
                <ResourceCard
                  href={resource.link}
                  onClick={handleCardClick(resource)}
                  target={resource.isExternal ? '_blank' : undefined}
                  rel={resource.isExternal ? 'noopener noreferrer' : undefined}
                >
                  <CardEyebrow>{resource.eyebrow}</CardEyebrow>
                  <CardTitle>{resource.title} <em>{resource.titleHighlight}</em></CardTitle>
                  <CardDescription>{resource.description}</CardDescription>
                  <CardMeta>
                    {resource.meta.map((m, i) => (
                      <span key={i}>
                        {m.label ? <><b>{m.value}</b> {m.label}</> : m.value}
                      </span>
                    ))}
                  </CardMeta>
                  <CardArrow>
                    {resource.cta} {resource.isExternal ? <ArrowUpRight size={16} color="#D9442B" /> : <ArrowRight size={16} color="#D9442B" />}
                  </CardArrow>
                </ResourceCard>
              </Fade>
            ))}
          </ResourcesGrid>

          <FooterSection>
            <span>&copy; 2026 Renee Louise</span>
            <Link to="/">reneelouise.tech</Link>
          </FooterSection>
        </Wrap>
      </main>
    </PageWrapper>
  );
};
